import type { components } from "@contracts/generated/editor-service";

import { OMISSION_REASONS, type OmissionReason } from "./previewInsights";

/**
 * Replay from a turn — the typed model behind the replay/fork UX (#1105,
 * approved design §11.1; the command is #1104's
 * `POST /v1/projects/{projectId}/preview-runs/{previewRunRef}/replays`).
 *
 * Three things live here, and they are here rather than in the components
 * because the fast PR gate (`src/lib`) proves this directory on every push:
 *
 *  1. WHICH TURN BOUNDARIES A RUN OFFERS. The source run's `turn` events —
 *     read as compact events, bodies BY REFERENCE and never text — are folded
 *     into one boundary per turn number, each either eligible for a replay or
 *     ineligible for a stated reason. Nothing is inferred: a turn the trace
 *     does not carry is not a boundary this browser can offer, and it says so.
 *
 *  2. WHAT THE BROWSER SENDS. `replayRequestBody` builds exactly the contract's
 *     fields — candidate, fixture, expected revision, idempotency key and the
 *     optional turn boundary — from selections the clinician made. No message
 *     body, hash, event ref, label or Liv/Brain identity has a field to ride
 *     in, and `src/invariants/previewEgress.test.tsx` holds that on the bytes.
 *
 *  3. WHAT EACH ANSWER MEANS. The command's three outcomes, five statuses and
 *     eighteen refusal codes are closed vocabularies transcribed from the
 *     vendored Tenant Liv contract; every one is mapped to a clinician-facing
 *     sentence and a recovery here, `satisfies` the generated unions, so a
 *     regenerated contract that grows a member fails the build by name rather
 *     than rendering a raw code.
 *
 * Only the synthetic user inputs are replayed; Liv's answers are produced
 * fresh by the chosen candidate, and the source run's answers, runtime state
 * and trace are never restored (ADR 0013 N8). The sentences that say so are
 * exported constants so the dialog and its tests cannot drift apart.
 */

export type ReplayPreviewRunRequest =
  components["schemas"]["ReplayPreviewRunRequest"];
export type PreviewReplayCreated = components["schemas"]["PreviewReplayCreated"];
export type PreviewReplayStatus = components["schemas"]["PreviewReplayStatus"];
export type PreviewReplayReasonCode =
  components["schemas"]["PreviewReplayReasonCode"];
export type PreviewReplayCommandOutcome =
  components["schemas"]["PreviewReplayCommandOutcome"];
export type PreviewRunEventPage = components["schemas"]["PreviewRunEventPage"];
export type PreviewTraceEventKind =
  components["schemas"]["PreviewTraceEventKind"];
type JsonValue = components["schemas"]["JsonValue"];
export type JsonRecord = { [key: string]: JsonValue };

/**
 * The preview target's published turn budget: the contract's `maximum` on
 * `ReplayPreviewRunRequest.stopAfterTurn`, transcribed exactly as the service
 * transcribes it from the vendored Tenant Liv contract
 * (`PREVIEW_REPLAY_MAX_TURNS`). A boundary above it is refused 422 before any
 * egress, so this browser does not offer one; `previewReplay.test.ts` pins
 * the figure against the OpenAPI document so the two cannot drift.
 */
export const PREVIEW_REPLAY_MAX_TURNS = 150;

/** The one event family this feature reads: the allowlisted `kind` filter. */
export const TURN_EVENT_KIND = "turn" satisfies PreviewTraceEventKind;

/**
 * Items per page when reading turn events — the contract's `limit` maximum,
 * pinned against the OpenAPI document by the test beside this module. A page
 * may still be SHORTER (the server also spends a byte budget), so pages are
 * followed on `hasMore`, never inferred from a short page.
 */
export const TURN_PAGE_LIMIT = 100;

/**
 * How many pages the turn read follows before it stops and SAYS it stopped.
 * The turn budget is forty and a page holds a hundred events, so a run that
 * needs more than this has a turn count this feature could not act on anyway;
 * the reading reports `truncated: true` rather than looping.
 */
export const MAX_TURN_PAGES = 5;

/** The closed event statuses `clinical-preview-trace.v1` publishes. */
export const TURN_EVENT_STATUSES = [
  "ok",
  "no_action",
  "attention",
  "failed",
  "unavailable",
] as const;
export type TurnEventStatus = (typeof TURN_EVENT_STATUSES)[number];

/** What a compact turn event says about its user input — by reference only. */
export type TurnInputReading =
  /** `state: inline | reference` — the input exists and is readable in detail. */
  | { state: "recorded"; byteSize: number | null }
  /** `state: omitted` — withheld, and the producer said why (or did not). */
  | { state: "withheld"; reason: OmissionReason | null }
  /** The slot is absent or not shaped as the contract shapes it. */
  | { state: "unreadable" };

export type TurnBoundaryIneligibility =
  /** The event carries no positive integer `coordinate.turnNumber`. */
  | { reason: "unnumbered" }
  /** The turn number is above the preview target's published budget. */
  | { reason: "beyond_turn_budget"; budget: number };

/**
 * One row of the boundary list: a compact `turn` event read into what the
 * panel renders and what a replay may name. Every field is read verbatim
 * from the event or is null; `label` is the producer's one-line
 * clinician-facing label (never a body), and no text of the input itself
 * exists on a compact event to read.
 */
export type TurnBoundary = {
  turnNumber: number | null;
  sequence: number | null;
  eventRef: string | null;
  occurredAt: string | null;
  label: string | null;
  status: TurnEventStatus | null;
  input: TurnInputReading;
  ineligibility: TurnBoundaryIneligibility | null;
};

export type TurnBoundariesReading = {
  /** In sequence order; one row per turn number (the first event that names it). */
  boundaries: TurnBoundary[];
  /** Later events naming a turn number an earlier one already named. */
  duplicateCount: number;
  /** Events on the page whose `kind` is not `turn` — the filter is server-side, so this is a contract disagreement. */
  foreignCount: number;
  /** Turn numbers absent between 1 and the highest numbered boundary. */
  missingTurnNumbers: number[];
};

function readRecord(value: JsonValue | undefined): JsonRecord | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as JsonRecord)
    : null;
}

function readString(value: JsonValue | undefined): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function readPositiveInt(value: JsonValue | undefined): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 1
    ? value
    : null;
}

function readNonNegativeInt(value: JsonValue | undefined): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : null;
}

function readStatus(value: JsonValue | undefined): TurnEventStatus | null {
  return typeof value === "string" &&
    (TURN_EVENT_STATUSES as readonly string[]).includes(value)
    ? (value as TurnEventStatus)
    : null;
}

function readInput(value: JsonValue | undefined): TurnInputReading {
  const content = readRecord(value);
  if (content === null) {
    return { state: "unreadable" };
  }
  const state = content["state"];
  if (state === "inline" || state === "reference") {
    return { state: "recorded", byteSize: readNonNegativeInt(content["byteSize"]) };
  }
  if (state === "omitted") {
    const reason = content["omittedReason"];
    return {
      state: "withheld",
      reason:
        typeof reason === "string" &&
        (OMISSION_REASONS as readonly string[]).includes(reason)
          ? (reason as OmissionReason)
          : null,
    };
  }
  return { state: "unreadable" };
}

/**
 * Folds a page of compact events into the boundaries a replay may name.
 *
 * ELIGIBILITY IS DECIDED FROM TWO FACTS AND NOTHING ELSE: the event names a
 * positive integer turn number, and that number is within the published turn
 * budget. Whether the input body was captured, withheld or restricted does
 * not enter into it — the preview target replays from ITS OWN record of the
 * source conversation, not from the trace, so a turn whose body the trace
 * could not carry is still a turn the target can replay. The reading shows
 * the body's state beside the row so a clinician can see what the trace
 * holds, and offers the boundary either way.
 *
 * A turn number the trace names twice is one boundary (the first event), and
 * the repeat is counted so the panel can say so; an event of another kind on
 * a page the server filtered is counted for the same reason. Numbers absent
 * between 1 and the highest eligible boundary are listed: a replay through
 * turn 4 replays turns 1–4 whether or not the trace shows turn 3, and a
 * clinician deciding on a boundary should know the view is not the script.
 */
export function readTurnBoundaries(events: JsonRecord[]): TurnBoundariesReading {
  const boundaries: TurnBoundary[] = [];
  const seen = new Set<number>();
  let duplicateCount = 0;
  let foreignCount = 0;

  for (const event of events) {
    if (event["kind"] !== TURN_EVENT_KIND) {
      foreignCount += 1;
      continue;
    }
    const coordinate = readRecord(event["coordinate"]);
    const detail = readRecord(event["detail"]);
    const turnNumber = readPositiveInt(coordinate?.["turnNumber"]);
    if (turnNumber !== null) {
      if (seen.has(turnNumber)) {
        duplicateCount += 1;
        continue;
      }
      seen.add(turnNumber);
    }
    boundaries.push({
      turnNumber,
      sequence: readPositiveInt(event["sequence"]),
      eventRef: readString(event["eventRef"]),
      occurredAt: readString(event["occurredAt"]),
      label: readString(event["label"]),
      status: readStatus(event["status"]),
      input: readInput(detail?.["userInput"]),
      ineligibility:
        turnNumber === null
          ? { reason: "unnumbered" }
          : turnNumber > PREVIEW_REPLAY_MAX_TURNS
            ? { reason: "beyond_turn_budget", budget: PREVIEW_REPLAY_MAX_TURNS }
            : null,
    });
  }

  const highest = Math.max(
    0,
    ...boundaries
      .filter((boundary) => boundary.ineligibility === null)
      .map((boundary) => boundary.turnNumber ?? 0),
  );
  const missingTurnNumbers: number[] = [];
  for (let number = 1; number < highest; number += 1) {
    if (!seen.has(number)) {
      missingTurnNumbers.push(number);
    }
  }

  return { boundaries, duplicateCount, foreignCount, missingTurnNumbers };
}

/** The boundaries a replay may actually name. */
export function eligibleBoundaries(reading: TurnBoundariesReading): TurnBoundary[] {
  return reading.boundaries.filter((boundary) => boundary.ineligibility === null);
}

/** The Brain's background phase: its track (and agent) while BG questions run. */
export const BACKGROUND_TRACK = "bg_sets";

/** The turn that left background; null too when that turn is beyond the replay budget. */
export function backgroundEndTurn(events: JsonRecord[]): number | null {
  const ends = events.flatMap((event) => {
    const detail = readRecord(event["detail"]);
    const before = readString(readRecord(detail?.["before"])?.["track"]);
    const after = readString(readRecord(detail?.["after"])?.["track"]);
    const turnNumber = readPositiveInt(readRecord(event["coordinate"])?.["turnNumber"]);
    return event["kind"] === TURN_EVENT_KIND &&
      before === BACKGROUND_TRACK &&
      after !== null &&
      after !== BACKGROUND_TRACK &&
      turnNumber !== null &&
      turnNumber <= PREVIEW_REPLAY_MAX_TURNS
      ? [turnNumber]
      : [];
  });
  return ends.length > 0 ? Math.min(...ends) : null;
}

/**
 * What a replay covers: the source run's inputs through one turn, or the
 * whole input script the preview target holds.
 */
export type ReplayBoundary =
  | { kind: "through_turn"; turnNumber: number }
  | { kind: "whole_script" };

/**
 * The fixed explanations every replay surface states (approved design §11.1;
 * the ticket's "explain that only synthetic user inputs are replayed and that
 * no state/assistant output is restored"). Constants, so the dialog and its
 * tests cannot say two different things.
 */
export const REPLAY_RULES = {
  inputsOnly:
    "Only the synthetic user inputs are replayed. Nothing a tester typed passes through this browser: the preview target reads the inputs from its own record of the source run.",
  nothingRestored:
    "Liv's answers are produced fresh by the testing version you choose. The source run's answers, its runtime state and its trace are not restored, copied or changed.",
  freshPreview:
    "The replay runs as a new candidate-bound synthetic preview with its own trace. The source run keeps answering exactly as it did.",
} as const;

/** One sentence naming exactly what the chosen boundary replays. */
export function describeReplayScope(
  boundary: ReplayBoundary,
  visibleTurnCount: number,
): string {
  const visible = `${visibleTurnCount} turn${visibleTurnCount === 1 ? "" : "s"}`;
  if (boundary.kind === "through_turn") {
    return boundary.turnNumber === 1
      ? `Turn 1 of the source run's synthetic user inputs is sent to the new conversation, and nothing after it. The trace shows ${visible}.`
      : `Turns 1 through ${boundary.turnNumber} of the source run's synthetic user inputs are sent, in order, to the new conversation, and nothing after turn ${boundary.turnNumber}. The trace shows ${visible}.`;
  }
  return `Every synthetic user input the preview target recorded for the source run is sent, in order, to the new conversation. The trace shows ${visible}; the target replays at most ${PREVIEW_REPLAY_MAX_TURNS}.`;
}

/**
 * Exactly the contract's fields, from exactly the clinician's selections.
 *
 * `stopAfterTurn` is OMITTED for a whole-script replay rather than sent as
 * null: the contract reads absence as "the whole input script", and a key
 * that is not on the wire cannot be misread. Nothing else has a field: the
 * source run is the locator in the path, and the target, its revision's
 * meaning, the destination's identity and the command id the target sees are
 * all resolved server-side (#1104).
 */
export function replayRequestBody(input: {
  candidateId: string;
  patientRef: string;
  expectedPreviewTargetRevision: number;
  idempotencyKey: string;
  boundary: ReplayBoundary;
}): ReplayPreviewRunRequest {
  return {
    candidateId: input.candidateId,
    patientRef: input.patientRef,
    expectedPreviewTargetRevision: input.expectedPreviewTargetRevision,
    idempotencyKey: input.idempotencyKey,
    // Plain replay; the Lab's "Continue from turn N" overrides this with true.
    continueManually: false,
    ...(input.boundary.kind === "through_turn"
      ? { stopAfterTurn: input.boundary.turnNumber }
      : {}),
  };
}

/** The number of turns a boundary plans, when it is knowable from here. */
export function plannedTurns(boundary: ReplayBoundary): number | null {
  return boundary.kind === "through_turn" ? boundary.turnNumber : null;
}

export const REPLAY_OUTCOMES = [
  "dispatched",
  "already_dispatched",
  "unresolved",
] as const satisfies readonly PreviewReplayCommandOutcome[];

export const REPLAY_STATUSES = [
  "queued",
  "running",
  "partial",
  "completed",
  "refused",
] as const satisfies readonly PreviewReplayStatus[];

export const REPLAY_REASON_CODES = [
  "capability_disabled",
  "destination_already_replayed",
  "destination_conversation_ended",
  "destination_not_fresh",
  "destination_not_preview",
  "destination_unavailable",
  "enqueue_failed",
  "idempotency_key_conflict",
  "run_budget_exhausted",
  "source_not_found",
  "source_revoked",
  "source_script_changed",
  "source_script_empty",
  "source_unavailable",
  "stop_after_turn_out_of_range",
  "target_mismatch",
  "turn_budget_exceeded",
  "turn_failed",
] as const satisfies readonly PreviewReplayReasonCode[];

/**
 * What ONE answer to the command means for the clinician.
 *
 * `dispatched` answers carry the target's own status; `already_dispatched`
 * and `unresolved` carry none, because the service re-sent nothing and has
 * nothing current to report (#1104's retry contract). A `dispatched` answer
 * without a status is a contract disagreement and is read as unreadable
 * rather than as any state.
 */
export type ReplayResultView = { previewRunRef: string } & (
  | { kind: "queued" }
  | { kind: "running" }
  | { kind: "completed" }
  | { kind: "partial"; reasonCode: PreviewReplayReasonCode | null }
  | { kind: "refused"; reasonCode: PreviewReplayReasonCode | null }
  | { kind: "already_dispatched" }
  | { kind: "unresolved" }
  | { kind: "unreadable" }
);

export function readReplayResult(created: PreviewReplayCreated): ReplayResultView {
  const previewRunRef = created.previewRunRef;
  switch (created.outcome) {
    case "already_dispatched":
      return { kind: "already_dispatched", previewRunRef };
    case "unresolved":
      return { kind: "unresolved", previewRunRef };
    case "dispatched":
      break;
    default:
      // The generated union protects builds that regenerate against a newer
      // contract; this guard protects a running older bundle when the server
      // is upgraded first.
      return { kind: "unreadable", previewRunRef };
  }
  const reasonCode =
    typeof created.reasonCode === "string" &&
    (REPLAY_REASON_CODES as readonly string[]).includes(created.reasonCode)
      ? created.reasonCode
      : null;
  switch (created.status ?? null) {
    case "queued":
      return { kind: "queued", previewRunRef };
    case "running":
      return { kind: "running", previewRunRef };
    case "completed":
      return { kind: "completed", previewRunRef };
    case "partial":
      return { kind: "partial", previewRunRef, reasonCode };
    case "refused":
      return { kind: "refused", previewRunRef, reasonCode };
    case null:
      return { kind: "unreadable", previewRunRef };
    default:
      return { kind: "unreadable", previewRunRef };
  }
}

/**
 * The one act that can change a refused answer, or `none` when there is none
 * a clinician can perform from this screen.
 */
export type ReplayRefusalRecovery =
  /** The source's inputs moved or are fewer than this view showed: re-read the turns and choose again. */
  | "refresh_turns"
  /** Pick an earlier boundary: the chosen one is past what the target will replay. */
  | "choose_earlier_turn"
  /** Start a fresh attempt with a new command id; nothing from this one is reused. */
  | "fresh_attempt"
  /** Try again later; the target's own budget or queue is the limit. */
  | "wait"
  /** A property of the deployment; an operator acts, not the clinician. */
  | "operator"
  /** The new run holds what was replayed before the failure: open it. */
  | "open_run"
  | "none";

export const REPLAY_REFUSAL_COPY = {
  capability_disabled: {
    title: "Replay is switched off on the preview target",
    description:
      "The preview target has its replay capability disabled in this deployment, so nothing was replayed. The new preview was created and holds no turns.",
    recovery: "operator",
  },
  destination_already_replayed: {
    title: "The new conversation had already received a replay",
    description:
      "The preview target reports that the conversation it created for this replay had already been replayed into, so it did not replay again.",
    recovery: "fresh_attempt",
  },
  destination_conversation_ended: {
    title: "The new conversation had already ended",
    description:
      "The preview target found the conversation it created for this replay already ended before any input could be sent, so nothing was replayed.",
    recovery: "fresh_attempt",
  },
  destination_not_fresh: {
    title: "The new conversation was not fresh",
    description:
      "The preview target only replays into a conversation with no turns, and the one it created for this replay was not empty. Nothing was replayed.",
    recovery: "fresh_attempt",
  },
  destination_not_preview: {
    title: "The destination is not a synthetic preview",
    description:
      "The preview target refused to replay because the conversation it created is not a candidate-bound synthetic preview. Nothing was replayed; this is a property of the deployment, not of your choices.",
    recovery: "operator",
  },
  destination_unavailable: {
    title: "The new conversation could not be reached",
    description:
      "The preview target could not reach the conversation it created for this replay, so nothing was replayed.",
    recovery: "fresh_attempt",
  },
  enqueue_failed: {
    title: "The preview target could not queue the replay",
    description:
      "The preview target accepted the command but could not queue the work, so nothing was replayed.",
    recovery: "fresh_attempt",
  },
  idempotency_key_conflict: {
    title: "This attempt's command id names a different command",
    description:
      "The preview target already holds a different replay command under this attempt's id, so it refused this one. Nothing was replayed and nothing about the other command is disclosed.",
    recovery: "fresh_attempt",
  },
  run_budget_exhausted: {
    title: "The preview target's replay budget is exhausted",
    description:
      "The preview target has spent its replay budget for now, so nothing was replayed and no model call was made. The budget is the target's, not this project's.",
    recovery: "wait",
  },
  source_not_found: {
    title: "The source run is not available to replay",
    description:
      "The preview target does not hold a synthetic preview it can read for the source run. It gives this one answer for an unknown run, a run that is not a synthetic preview, a real session and an ambiguous match alike, so nothing more is known here. Nothing was replayed.",
    recovery: "none",
  },
  source_revoked: {
    title: "The source run's content was revoked",
    description:
      "The preview target revoked the source run's content, so its inputs can no longer be replayed. Nothing was replayed.",
    recovery: "none",
  },
  source_script_changed: {
    title: "The source run's inputs changed since this boundary was chosen",
    description:
      "The preview target's record of the source run's inputs no longer matches what this replay named, so nothing was replayed. Re-read the turns and choose a boundary again.",
    recovery: "refresh_turns",
  },
  source_script_empty: {
    title: "The source run has no synthetic user input to replay",
    description:
      "The preview target holds no user input for the source run, so there is nothing to replay yet. Nothing was replayed.",
    recovery: "refresh_turns",
  },
  source_unavailable: {
    title: "The source run's content is no longer available",
    description:
      "The preview target no longer holds the source run's inputs — its content has expired or was removed — so a replay from it is no longer possible. Nothing was replayed.",
    recovery: "none",
  },
  stop_after_turn_out_of_range: {
    title: "The chosen turn is past the source run's last input",
    description:
      "The preview target's record of the source run holds fewer inputs than the boundary named, so it refused rather than clamp. Nothing was replayed; choose an earlier turn after re-reading the trace.",
    recovery: "refresh_turns",
  },
  target_mismatch: {
    title: "The preview target could not match the source run to this replay",
    description:
      "The preview target reports that the source run's binding does not match the one this replay was dispatched under, and refused it. Nothing was replayed. This is a property of the preview target's replay contract in this deployment, not of the turn or candidate you chose.",
    recovery: "operator",
  },
  turn_budget_exceeded: {
    title: "The script is longer than the preview target's turn budget",
    description: `The preview target replays at most ${PREVIEW_REPLAY_MAX_TURNS} turns and the source run's inputs exceed that, so it refused the whole replay rather than part of it. Nothing was replayed.`,
    recovery: "choose_earlier_turn",
  },
  turn_failed: {
    title: "A turn failed during the replay",
    description:
      "The preview target could not deliver one of the source run's inputs to the new conversation and stopped the replay there. Whatever it delivered before that stands in the new run, which is marked incomplete and shows exactly what ran — possibly nothing.",
    recovery: "open_run",
  },
} as const satisfies Record<
  PreviewReplayReasonCode,
  { title: string; description: string; recovery: ReplayRefusalRecovery }
>;

/** For a refusal the target coded in a vocabulary this build does not enumerate. */
export const REPLAY_REFUSAL_UNKNOWN = {
  title: "The preview target refused the replay",
  description:
    "The preview target refused this replay for a reason this Editor build does not recognise, so nothing was replayed and the reason is not repeated here. The new preview was created and holds no turns.",
  recovery: "operator",
} as const satisfies { title: string; description: string; recovery: ReplayRefusalRecovery };

export function describeReplayRefusal(reasonCode: PreviewReplayReasonCode | null): {
  title: string;
  description: string;
  recovery: ReplayRefusalRecovery;
} {
  return reasonCode === null ||
    !(REPLAY_REASON_CODES as readonly string[]).includes(reasonCode)
    ? REPLAY_REFUSAL_UNKNOWN
    : REPLAY_REFUSAL_COPY[reasonCode];
}

/**
 * How far a replay has got, read from the DESTINATION run's own turn events:
 * every turn the new conversation has recorded is a source input that was
 * delivered and answered. `planned` is the boundary's turn count, or null for
 * a whole-script replay whose length only the target knows — in which case
 * completion is never claimed here.
 */
export type ReplayProgress = {
  replayedTurns: number;
  planned: number | null;
  complete: boolean;
};

export function readReplayProgress(
  destination: TurnBoundariesReading,
  planned: number | null,
): ReplayProgress {
  const replayedTurns = destination.boundaries.filter(
    (boundary) => boundary.turnNumber !== null,
  ).length;
  return {
    replayedTurns,
    planned,
    complete: planned !== null && replayedTurns >= planned,
  };
}

export function describeReplayProgress(progress: ReplayProgress): string {
  const turns = `${progress.replayedTurns} turn${progress.replayedTurns === 1 ? "" : "s"}`;
  if (progress.planned === null) {
    return `${turns} replayed so far. The whole script's length is known only to the preview target, so completion is read from the run itself.`;
  }
  if (progress.complete) {
    return `All ${progress.planned} planned turn${progress.planned === 1 ? "" : "s"} replayed.`;
  }
  return `${progress.replayedTurns} of ${progress.planned} planned turns replayed so far.`;
}

export const TURN_STATUS_LABELS = {
  ok: "Answered",
  no_action: "No action",
  attention: "Needs attention",
  failed: "Failed",
  unavailable: "Unavailable",
} as const satisfies Record<TurnEventStatus, string>;

export const REPLAY_RESULT_LABELS = {
  queued: "Queued",
  running: "Running",
  completed: "Completed",
  partial: "Partial",
  refused: "Refused",
  already_dispatched: "Already started",
  unresolved: "Unresolved",
  unreadable: "Unreadable",
} as const satisfies Record<ReplayResultView["kind"], string>;
